<template>
  <div class="proofreading-preview-plan">
    <h1 class="proofreading-preview-plan__heading">
      {{ $t("proofreadingPreviewPlanPage.heading") }}
    </h1>

    <div v-if="isLoading" class="proofreading-preview-plan__loading">
      <LoadingSpinner size="small" variant="inline" :text="$t('proofreadingPreviewPlanPage.loading')" />
    </div>

    <div v-else class="proofreading-preview-plan__content">
      <section class="preview-section">
        <h2 class="preview-section__title">{{ $t("proofreadingPreviewPlanPage.novelSectionTitle") }}</h2>
        <dl class="preview-field-list">
          <div v-for="field in novelInfoFields" :key="field.name" class="preview-field">
            <dt class="preview-field__label" :class="{ 'preview-field__label--empty': !field.filled }">
              {{ field.label }}
            </dt>
            <dd
              class="preview-field__value"
              :class="{ 'preview-field__value--empty': !field.filled }"
            >{{ field.value || $t("proofreadingPreviewPlanPage.emptyValue") }}</dd>
          </div>
        </dl>
      </section>

      <section class="preview-section">
        <h2 class="preview-section__title">{{ $t("proofreadingPreviewPlanPage.themeSectionTitle") }}</h2>
        <dl class="preview-field-list">
          <div
            v-for="block in themePreviewBlocks"
            :key="block.kind === 'field' ? block.field.name : 'target'"
            class="preview-field"
          >
            <template v-if="block.kind === 'field'">
              <dt class="preview-field__label" :class="{ 'preview-field__label--empty': !block.field.filled }">
                {{ block.field.label }}
              </dt>
              <dd
                class="preview-field__value"
                :class="{ 'preview-field__value--empty': !block.field.filled }"
              >{{ block.field.value || $t("proofreadingPreviewPlanPage.emptyValue") }}</dd>
            </template>
            <template v-else>
              <dt class="preview-field__label">{{ block.title }}</dt>
              <dd class="preview-field__target">
                <div
                  v-for="field in block.fields"
                  :key="field.name"
                  class="preview-field__nested"
                >
                  <p
                    class="preview-field__sub-label"
                    :class="{ 'preview-field__sub-label--empty': !field.filled }"
                  >{{ field.label }}</p>
                  <p
                    class="preview-field__value"
                    :class="{ 'preview-field__value--empty': !field.filled }"
                  >{{ field.value || $t("proofreadingPreviewPlanPage.emptyValue") }}</p>
                </div>
              </dd>
            </template>
          </div>
        </dl>
      </section>

    </div>

    <div class="proofreading-preview-plan__footer">
      <button
        type="button"
        class="proofreading-preview-plan__back-button"
        :disabled="isLoading || isConfirmingIncomplete"
        @click="onClickBack"
      >
        {{ $t("proofreadingPreviewPlanPage.back") }}
      </button>
      <button
        type="button"
        class="proofreading-preview-plan__next-button"
        :disabled="isLoading || isConfirmingIncomplete"
        @click="onClickNext"
      >
        {{ $t("proofreadingPreviewPlanPage.next") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import ProofreadingIncompleteThemeDialog, {
  ProofreadingIncompleteThemeDialogResult,
} from "@/components/ui/dialogs/ProofreadingIncompleteThemeDialog.vue";
import { Dialog } from "@/lib/utils/dialog";
import { useThemeStore } from "@/stores/theme";
import { useProofreadingAiStore } from "@/stores/proofreadingAi";
import {
  THEME_FIELD_LABEL_KEYS,
  type ThemeFieldName,
} from "@/constants/themeReview";
import type { Novel } from "@/lib/models";

/** 企画画面の「作品のターゲット層」にまとめて出す項目。 */
const TARGET_THEME_FIELD_NAMES: readonly ThemeFieldName[] = [
  "target-segment",
  "target-preference",
  "target-prefer-novels",
];

/** 企画画面（ThemeEditor）の入力順。AI校正の参照確認もこれに合わせる。 */
const AI_PROOFREADING_THEME_FIELD_ORDER = [
  "synopsis",
  "characters",
  "goal",
  "theme",
  "story-changes",
  "logline",
  "target-segment",
  "target-preference",
  "target-prefer-novels",
  "word-length",
] as const satisfies readonly ThemeFieldName[];

type PreviewField = {
  name: string;
  label: string;
  value: string;
  filled: boolean;
};

type ThemePreviewBlock =
  | { kind: "field"; field: PreviewField }
  | { kind: "target"; title: string; fields: PreviewField[] };

export default Vue.extend({
  components: {
    LoadingSpinner,
  },
  props: {
    novelId: String,
  },
  data() {
    return {
      proofreadingAiStore: useProofreadingAiStore(),
      isLoading: true,
      isConfirmingIncomplete: false,
    };
  },
  computed: {
    novel(): Novel | undefined {
      if (!this.novelId) return undefined;
      return this.$store.getters["novelModule/novel"](this.novelId) as Novel | undefined;
    },
    themeDetails(): { name: string; value: string }[] {
      const themeStore = useThemeStore();
      return themeStore.theme?.detail ?? [];
    },
    novelInfoFields(): PreviewField[] {
      const genreLabel = this.novel?.genre
        ? (this.$t(`common.novelSettings.genre.${this.novel.genre}`) as string)
        : "";
      const categoryLabel = (this.novel?.category ?? [])
        .map((c) => this.$t(`common.novelSettings.category.${c}`) as string)
        .join(" / ");

      return [
        {
          name: "title",
          label: this.$t("novelDetail.title") as string,
          value: this.novel?.title?.trim() ?? "",
          filled: !!this.novel?.title?.trim(),
        },
        {
          name: "genre",
          label: this.$t("homePage.novelList.filter.genre") as string,
          value: genreLabel,
          filled: !!this.novel?.genre,
        },
        {
          name: "category",
          label: this.$t("homePage.novelList.filter.category") as string,
          value: categoryLabel,
          filled: (this.novel?.category?.length ?? 0) > 0,
        },
      ];
    },
    themeFields(): PreviewField[] {
      return AI_PROOFREADING_THEME_FIELD_ORDER.map((name) => {
        const value = this.themeDetails.find((detail) => detail.name === name)?.value?.trim() ?? "";
        return {
          name,
          label: this.$t(THEME_FIELD_LABEL_KEYS[name]) as string,
          value,
          filled: value.length > 0,
        };
      });
    },
    themePreviewBlocks(): ThemePreviewBlock[] {
      const targetNames = new Set<string>(TARGET_THEME_FIELD_NAMES);
      const targetFields = this.themeFields.filter((field) => targetNames.has(field.name));
      const blocks: ThemePreviewBlock[] = [];
      let targetInserted = false;

      this.themeFields.forEach((field) => {
        if (targetNames.has(field.name)) {
          if (!targetInserted) {
            blocks.push({
              kind: "target",
              title: this.$t("themePage.targetTitle") as string,
              fields: targetFields,
            });
            targetInserted = true;
          }
          return;
        }
        blocks.push({ kind: "field", field });
      });

      return blocks;
    },
    hasIncompletePlanFields(): boolean {
      return (
        this.novelInfoFields.some((field) => !field.filled)
        || this.themeFields.some((field) => !field.filled)
      );
    },
  },
  async mounted() {
    if (this.novelId) {
      this.proofreadingAiStore.setActiveNovelId(this.novelId);
      this.proofreadingAiStore.setMode("ai");
    }
    await this.loadPreviewData();
  },
  methods: {
    async loadPreviewData() {
      if (!this.novelId) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      try {
        const themeStore = useThemeStore();
        await themeStore.fetchTheme(this.novelId);
      } finally {
        this.isLoading = false;
      }
    },
    onClickNext() {
      if (!this.novelId) return;
      if (this.hasIncompletePlanFields) {
        this.confirmIncompletePlanThenProceed();
        return;
      }
      this.goToSelectManuscript();
    },
    async confirmIncompletePlanThenProceed() {
      if (!this.novelId || this.isConfirmingIncomplete) return;
      this.isConfirmingIncomplete = true;
      try {
        const dialog = new Dialog(ProofreadingIncompleteThemeDialog);
        const result = (await dialog.show()) as
          | ProofreadingIncompleteThemeDialogResult
          | undefined;
        if (result === "ok") {
          this.goToSelectManuscript();
          return;
        }
        if (result === "editTheme") {
          this.$router.push({
            name: "theme",
            params: { novelId: this.novelId },
          });
        }
      } finally {
        this.isConfirmingIncomplete = false;
      }
    },
    goToSelectManuscript() {
      if (!this.novelId) return;
      this.$router.push({
        name: "proofreadingSelectManuscript",
        params: { novelId: this.novelId },
        query: { mode: "ai" },
      });
    },
    onClickBack() {
      if (!this.novelId) return;
      this.$router.push({
        name: "proofreadingSelectType",
        params: { novelId: this.novelId },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-preview-plan {
  display: flex;
  flex-direction: column;
  max-width: 640px;
  min-height: 100%;
  margin: 0 auto;
  padding: 40px 24px;
  color: var(--color-text-normal);
}

.proofreading-preview-plan__heading {
  margin: 0 0 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  text-align: left;
  color: var(--color-text-normal);
}

.proofreading-preview-plan__loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.proofreading-preview-plan__content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-section {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  padding: 16px 0;
}

.preview-section__title {
  margin: 0 0 16px;
  padding-bottom: 8px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  border-bottom: 1px solid var(--color-text-normal);
}

.preview-section__empty,
.preview-section__more {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

.preview-field-list {
  margin: 0;
}

.preview-field {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.preview-field__label {
  margin: 0 0 4px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);

  &--empty {
    color: var(--color-text-low);
    text-decoration: line-through;
  }
}

.preview-field__target {
  margin: 0;
}

.preview-field__nested {
  margin-top: 12px;
}

.preview-field__sub-label {
  margin: 0 0 4px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);

  &--empty {
    color: var(--color-text-low);
    text-decoration: line-through;
  }
}

.preview-field__value {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  white-space: pre-wrap;
  word-break: break-word;

  &--empty {
    color: var(--color-text-low);
    font-style: italic;
  }
}

.proofreading-preview-plan__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.proofreading-preview-plan__back-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 32px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  background-color: #ebebeb;
  color: var(--color-gray-1);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proofreading-preview-plan__next-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 48px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  background-color: var(--color-primary);
  color: var(--color-text-invert);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@include mq("sp") {
  .proofreading-preview-plan {
    padding: 24px 16px;
  }
}
</style>
